import React, { useCallback, useContext } from "react";
import Head from "next/head";
import Link from "next/link";

import {
  BuildingPageQuery_buildingOrRedirect_building,
  BuildingPageQuery_buildingOrRedirect_building_listings,
} from "types/pages/building/BuildingPageQuery";
import Layout from "components/layout";
import SaveIcon from "components/Save/Icon";
import {
  formatBuildingSeats,
  formatBuildingSqf,
  formatSeats,
  formatSqf,
  getPriceByLeaseType,
  getPsfOrFallback,
  sortListingsByPsf,
} from "util/listingsUtil";
import { BASE_URL } from "config";
import { useCurrentUser } from "hooks";
import ChatCTA from "components/ChatCTA";
import BrokerInsight from "components/BrokerInsight";
import cn from "classnames";
import {
  getBuildingMetaData,
  getBuildingMetaDescription,
  getMarketLink,
  getNeighborhoodLink,
  getSubmarketLink,
} from "util/seo";
import { constantCase } from "util/strings";

import NoOfEmployeesEngagementBox from "components/cards/NoOfEmployeesEngagementBox";
import Availability from "components/BuildingAvailability";
import AvailabilityMobile from "components/BuildingAvailability/MobileView";
import { analyticsEvents, track } from "util/analytics";
import MobileContext from "context/MobileContext";
import styles from "./styles.module.scss";
import RecentlyUpdated, {
  shouldRenderRecentlyUpdate,
} from "./RecentlyUpdatedBadge";
import AboutTheListing from "./AboutTheListing";
import AboutTheBuilding from "./AboutTheBuilding";
import BuildingImageCarousel from "./BuildingImageCarousel";
import InquiryButtons from "./CTAButtons";
import SimliarOffices from "./SimilarOffices";

interface IProps {
  selectedListing?: BuildingPageQuery_buildingOrRedirect_building_listings | null;
  building: BuildingPageQuery_buildingOrRedirect_building;
  state: string;
  city: string;
  address: string;
  prevUrl?: string;
}

export default function BuildingPage({
  selectedListing,
  building,
  prevUrl,
}: IProps) {
  const { currentUser } = useCurrentUser();
  const isMobileView = useContext(MobileContext);

  const displayAddress = `${building.address}${
    selectedListing ? `, ${selectedListing.floorAndSuite}` : ""
  }`;

  let size;
  let price;
  let seats;
  let lease;
  const renderSeats = building.listings && building.listings.length > 0;

  if (selectedListing) {
    size = formatSqf(selectedListing);
    seats = formatSeats(selectedListing);

    const priceInfo = getPriceByLeaseType(selectedListing);

    price = priceInfo ? (
      <>
        {priceInfo.monthly && <p>{priceInfo.monthly} monthly</p>}
        {priceInfo.psf && <p>({priceInfo.psf} psf)</p>}
      </>
    ) : (
      <p>Inquire for pricing</p>
    );

    lease = selectedListing.details["Lease Term"] &&
      selectedListing.details["Lease Type"] && (
        <>
          <p>{selectedListing.details["Lease Term"]}</p>
          <p>{selectedListing.details["Lease Type"]}</p>
        </>
      );
  } else {
    size = formatBuildingSqf(building);
    seats = formatBuildingSeats(building);

    const nhoodPsf = building.neighborhood?.yearlyPricePerFoot
      ? building.neighborhood?.yearlyPricePerFoot / 12
      : 0;
    const sortedlistingsPSF = sortListingsByPsf(building.listings || []);
    const hasListings = !!sortedlistingsPSF.length;
    const endex = sortedlistingsPSF.length - 1;
    price = (
      <>
        <p>
          <span>${building.minEstimatedMonthlyPrice || 0}</span>
          <span> - ${building.maxEstimatedMonthlyPrice || 0} monthly</span>
        </p>
        <p>
          <span>
            ($
            {hasListings
              ? getPsfOrFallback(sortedlistingsPSF[0], nhoodPsf)
              : nhoodPsf}
          </span>
          <span>
            {" "}
            - $
            {hasListings
              ? getPsfOrFallback(sortedlistingsPSF[endex], nhoodPsf)
              : nhoodPsf}{" "}
            psf)
          </span>
        </p>
      </>
    );
  }
  const showAboutTheListing =
    selectedListing &&
    (selectedListing.description ||
      selectedListing.naturalLight ||
      selectedListing.hasWetPantry ||
      selectedListing.floorMaterial ||
      selectedListing.conferenceRooms ||
      selectedListing.hasExposedCeiling ||
      selectedListing.hasPrivateBathroom);

  const showAbouttheBuilding =
    building?.details["Wired Certification"] ||
    building?.details["Walk Score®"] ||
    building?.details["Noise Index"] ||
    building?.details.Floors ||
    building?.cmsContent ||
    building?.hasTurnstile ||
    building?.hasRoundTheClockAccess ||
    building?.hasAttendedLobby ||
    building?.hasVirtualTours;

  const getBackLink = useCallback(() => {
    // prevUrl is the referrer from req headers. If it includes office-space then we are assuming that is our public search page and will link back to it.
    // this preserves the query string params. If it does not then its from another source that won't have query string params so we will link to the buildings neighborhood.
    // if someone referred from somewhere like Google or another backlink this will keep them within squarefoot.com
    if (prevUrl?.includes("/office-space")) return prevUrl;
    if (building.neighborhood) return getNeighborhoodLink(building);
    if (building.submarket) return getSubmarketLink(building);
    if (building.city && building.state) return getMarketLink(building);
    return null;
  }, [prevUrl, building]);
  const backLink = getBackLink();
  return (
    <Layout>
      <Head>
        <title>
          {building.address},{" "}
          {building.neighborhood ? `${building.neighborhood.name}, ` : ""}
          {building.city}, {building.state} {building.zip} | SquareFoot
        </title>
        <script
          type="application/ld+json"
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{
            __html: getBuildingMetaData(building, !!selectedListing),
          }}
        />
        <meta
          name="description"
          content={getBuildingMetaDescription(building)}
        />
        <meta name="geo.placename" content="United States" />
        <meta name="geo.region" content="usa" />
        <meta name="geo.position" content={`${building.lat},${building.lng}`} />
        {!building.listings?.length && <meta name="robots" content="noindex" />}
        <link
          rel="canonical"
          href={`${BASE_URL}/building${building.slug}/${building.identity}`}
        />
      </Head>
      <div className="inset">
        <BuildingImageCarousel
          building={building}
          selectedListing={selectedListing}
          displayAddress={displayAddress}
        />
        {backLink && (
          <Link href={backLink} passHref>
            <a className={styles.link}>Return to search</a>
          </Link>
        )}
        <div className={styles.container}>
          <main className={styles.main}>
            <div className={styles.heading}>
              <h1 className={styles.addressHeading}>{displayAddress}</h1>
              {selectedListing && (
                <SaveIcon
                  id={selectedListing.id}
                  buildingId={building.id}
                  signUpType="Listing"
                  source="Bookmark"
                  areaExternalIds={[building.neighborhood?.externalId]}
                  marketOfInterest={building.market?.externalId}
                  cityOfInterest={constantCase(building.city)}
                  bookMarkNotSavedClass={styles.bookmarkNotSaved}
                  trackingEvent={() =>
                    track(analyticsEvents.clicked, {
                      category: "Listing Page CTAs",
                      label: "Bookmark",
                    })
                  }
                />
              )}
            </div>
            <nav
              data-qa="building-page-breadcrumbs"
              className={styles.breadcrumbs}
            >
              <ul>
                {building.city && building.state && (
                  <li className={styles.crumb}>
                    <Link href={getMarketLink(building)}>
                      {building.market?.name || building.city}
                    </Link>
                  </li>
                )}
                {building.submarket && (
                  <li className={styles.crumb}>
                    <Link href={getSubmarketLink(building)}>
                      {building.submarket.name}
                    </Link>
                  </li>
                )}
                {building.neighborhood && (
                  <li className={styles.crumb}>
                    <Link href={getNeighborhoodLink(building)}>
                      {building.neighborhood.name}
                    </Link>
                  </li>
                )}
                {selectedListing?.id && (
                  <li className={styles.crumb}>
                    <Link
                      href={`/building${building.slug}/${building.identity}`}
                    >
                      {building.address}
                    </Link>
                  </li>
                )}
              </ul>
            </nav>
            <section className={cn(styles.infoOutter, styles.section)}>
              <div className={styles.infoInner}>
                <p className={styles.subHeading}>Pricing</p>
                {price}
              </div>
              <div className={styles.infoInner}>
                <p className={styles.subHeading}>Size</p>
                <>
                  {size && <p>{size} sf</p>}

                  {selectedListing && seats && renderSeats && (
                    <p>{seats} employees</p>
                  )}
                </>
              </div>
              {!selectedListing && seats && renderSeats && (
                <div className={styles.infoInner}>
                  <p className={styles.subHeading}>Seats</p>
                  <p>{seats} employees</p>
                </div>
              )}
              {selectedListing && lease && (
                <div className={styles.infoInner}>
                  <p className={styles.subHeading}>Lease Term</p>
                  <p>{lease}</p>
                </div>
              )}
            </section>

            {selectedListing?.dataChangedAt &&
              shouldRenderRecentlyUpdate(selectedListing.dataChangedAt) && (
                <section className={styles.section}>
                  <RecentlyUpdated />
                </section>
              )}

            <section className={cn(styles.chatCTAContainer, styles.section)}>
              <h2>Chat with a SquareFoot Specialist</h2>
              <ChatCTA
                market={building?.market?.id || ""}
                areas={
                  building.neighborhood?.externalId
                    ? [building.neighborhood]
                    : []
                }
                address={displayAddress}
                city={building?.market?.id ? undefined : building.city}
                buildingSlug={building.slug}
                listingId={selectedListing?.id}
                signUpType={selectedListing ? "Listing" : "Building"}
                isLoggedIn={!!currentUser}
              />
            </section>

            {showAboutTheListing && (
              <section className={styles.section}>
                <AboutTheListing
                  description={selectedListing.description}
                  naturalLight={selectedListing.naturalLight}
                  hasWetPantry={selectedListing.hasWetPantry}
                  floorMaterial={selectedListing.floorMaterial}
                  conferenceRooms={selectedListing.conferenceRooms}
                  hasExposedCeiling={selectedListing.hasExposedCeiling}
                  hasPrivateBathroom={selectedListing.hasPrivateBathroom}
                />
              </section>
            )}

            {showAbouttheBuilding && !selectedListing && (
              <section className={styles.section}>
                <AboutTheBuilding
                  details={building.details}
                  description={building.cmsContent?.content || ""}
                  hasAttendedLobby={!!building.hasAttendedLobby}
                  hasRoundTheClockAccess={!!building.hasRoundTheClockAccess}
                  hasTurnstile={!!building.hasTurnstile}
                  hasVirtualTour={!!building.hasVirtualTours}
                  address={building.address}
                  fullAddress={building.fullAddress}
                  addresses={
                    building.addresses?.flatMap((l) => (l ? [l] : [])) || []
                  }
                  name={building.propertyName || ""}
                />
              </section>
            )}
            {selectedListing?.brokerInsights?.[0] && (
              <BrokerInsight
                brokerInsight={selectedListing.brokerInsights[0]}
                className={cn(styles.section, styles.brokerInsight)}
              />
            )}
            {!currentUser && (
              <NoOfEmployeesEngagementBox
                market={building.market?.id}
                city={building.city}
                areas={building.neighborhood ? [building.neighborhood] : []}
                lead={selectedListing ? "Listing" : "Building"}
                source="External"
                listingId={selectedListing?.id}
              />
            )}

            {showAbouttheBuilding && selectedListing && (
              <section className={styles.section}>
                <AboutTheBuilding
                  details={building.details}
                  description={building.cmsContent?.content || ""}
                  hasAttendedLobby={!!building.hasAttendedLobby}
                  hasRoundTheClockAccess={!!building.hasRoundTheClockAccess}
                  hasTurnstile={!!building.hasTurnstile}
                  hasVirtualTour={!!building.hasVirtualTours}
                  address={building.address}
                  fullAddress={building.fullAddress}
                  addresses={
                    building.addresses?.flatMap((l) => (l ? [l] : [])) || []
                  }
                  name={building.propertyName || ""}
                />
              </section>
            )}

            <section className={styles.section}>
              <h1>Availability</h1>
              {isMobileView ? (
                <AvailabilityMobile
                  buildingId={building.id}
                  type={selectedListing ? "Listing" : "Building"}
                />
              ) : (
                <Availability
                  buildingId={building.id}
                  selectedListingId={selectedListing?.id}
                />
              )}
            </section>
          </main>
          <InquiryButtons
            buildingId={building.id}
            selectedListingId={selectedListing?.id}
            displayAddress={displayAddress}
            marketOfInterest={building?.market?.externalId}
            areaExternalIds={
              building?.neighborhood?.externalId
                ? [building?.neighborhood?.externalId]
                : []
            }
            cityOfInterest={constantCase(building.city)}
          />
        </div>
        <section className={styles.section}>
          <SimliarOffices
            isLoggedIn={!!currentUser}
            buildingId={building.id}
            type={selectedListing ? "Listing" : "Building"}
          />
        </section>
      </div>
    </Layout>
  );
}
